import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Overline } from "../components/Overline";
import { PROGRAMS } from "../lib/site";
import { Seo } from "../components/Seo";

export default function WorkWithMe() {
  return (
    <div data-testid="page-work-with-me" className="bg-[#faf9f6]">
      <Seo title="Work With Me" description="Three ways to begin: discovery call, single consultation, and the Aima Foundation and Aima Flourish programmes for real, lasting hormonal change." path="/work-with-me" />
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <div className="max-w-3xl">
          <Overline>Work with me</Overline>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.04] font-normal text-[#333] mt-5">
            Three ways
            <br />
            <span className="italic">we can begin.</span>
          </h1>
          <p className="mt-8 text-lg text-[#3d3d3d] font-normal leading-relaxed">
            Real hormonal change is not a 30-day plan. My programmes are built
            around the timelines your biology actually needs - three months,
            six months, or a single, focused session for clarity.
          </p>
          <p className="mt-5 text-base text-[#5a5a5a] font-normal">
            Not sure which one is right for you?{" "}
            <a
              href="https://calendly.com/aimawomenshealth/freecall"
              target="_blank"
              rel="noreferrer"
              data-testid="workwithme-not-sure-link"
              className="text-[#5fa292] underline underline-offset-4 decoration-[#5fa292]/40 hover:decoration-[#5fa292] transition-colors font-medium"
            >
              Book a free 20-minute discovery call
            </a>{" "}
            and we'll find your fit together.
          </p>
        </div>

        <div className="mt-20 grid lg:grid-cols-2 gap-6">
          {PROGRAMS.map((p) => (
            <div
              key={p.slug}
              data-testid={`program-${p.slug}`}
              className={`relative rounded-3xl p-10 border transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#5fa292]/5 ${
                p.featured
                  ? "bg-[#5fa292] text-white border-[#5fa292]"
                  : "bg-white text-[#333] border-[#e5e0d8]"
              }`}
            >
              {p.featured && (
                <div className="absolute top-6 right-6 text-[10px] uppercase tracking-[0.25em] bg-[#cbdad6] text-[#333] px-3 py-1 rounded-full">
                  Most chosen
                </div>
              )}
              <p
                className={`text-xs uppercase tracking-[0.25em] ${
                  p.featured ? "text-white/80" : "text-[#5fa292]"
                }`}
              >
                {p.price}
              </p>
              <h2
                className={`font-serif text-3xl md:text-4xl mt-3 tracking-tight font-normal ${
                  p.featured ? "text-white" : "text-[#333]"
                }`}
              >
                {p.name}
              </h2>
              <p
                className={`mt-5 font-normal leading-relaxed ${
                  p.featured ? "text-white/90" : "text-[#3d3d3d]"
                }`}
              >
                {p.description}
              </p>
              <ul className="mt-7 space-y-3">
                {p.includes.map((i) => (
                  <li
                    key={i}
                    className={`flex items-start gap-3 text-sm font-normal ${
                      p.featured ? "text-white/95" : "text-[#3d3d3d]"
                    }`}
                  >
                    <Check
                      size={16}
                      className={
                        p.featured ? "text-[#cbdad6] mt-0.5" : "text-[#5fa292] mt-0.5"
                      }
                    />
                    {i}
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href={p.ctaHref || "/contact"}
                  target={p.ctaHref?.startsWith("http") ? "_blank" : undefined}
                  rel={p.ctaHref?.startsWith("http") ? "noreferrer" : undefined}
                  data-testid={`program-cta-${p.slug}`}
                >
                  <Button
                    className={
                      p.featured
                        ? "bg-white text-[#5fa292] hover:bg-[#faf9f6] rounded-full px-8 h-12 w-full sm:w-auto"
                        : "bg-[#5fa292] hover:bg-[#4d8275] text-white rounded-full px-8 h-12 w-full sm:w-auto"
                    }
                  >
                    {p.cta}
                  </Button>
                </a>
                {p.secondaryCta && (
                  <a
                    href={p.secondaryCtaHref}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`program-cta-secondary-${p.slug}`}
                  >
                    <Button
                      variant="outline"
                      className={
                        p.featured
                          ? "border-white text-white hover:bg-white/10 rounded-full px-8 h-12 w-full sm:w-auto bg-transparent"
                          : "border-[#5fa292] text-[#5fa292] hover:bg-[#cbdad6]/40 rounded-full px-8 h-12 w-full sm:w-auto bg-transparent"
                      }
                    >
                      {p.secondaryCta}
                    </Button>
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-20 max-w-3xl">
          <Overline>FAQ</Overline>
          <h3 className="font-serif text-3xl md:text-4xl text-[#333] mt-4 font-normal tracking-tight">
            A few things you may be wondering.
          </h3>
          <div className="mt-10 divide-y divide-[#e5e0d8]">
            {[
              {
                q: "Do you replace my doctor?",
                a: "No. I work alongside your medical team, often filling the gaps, giving you the depth and context they don't have time or training for. While women's health and wellbeing is not at the forefront of medical research or training, it is my dedicated focus. I'll always tell you when something needs medical attention and is not within my scope.",
              },
              {
                q: "Will I need lab testing?",
                a: "Not always. Sometimes story and symptoms are enough. When testing is genuinely useful, I'll recommend it and explain why - giving you informed options. Often I use a DUTCH test for a fuller metabolic picture instead of simple blood tests.",
              },
              {
                q: "Where are you based?",
                a: "I work entirely online. Clients join me from across the UK, Europe, North America and Australia.",
              },
              {
                q: "Do you offer payment plans?",
                a: "Real change requires commitment from both of us. The 3-month programme is an upfront payment. The 6-month programme can be split into two payments. However, accessibility matters to me, so other arrangements will be considered on a case-by-case basis. I work with women and girls who are all in and ready to go.",
              },
            ].map((f) => (
              <div key={f.q} className="py-6">
                <p className="font-serif text-xl text-[#333] font-normal">
                  {f.q}
                </p>
                <p className="mt-2 text-[#3d3d3d] font-normal leading-relaxed">
                  {f.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
